import {
  Alert,
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { useExternalRegisterImport } from "./externalRegisterImportProvider";
import { compactTableSx } from "../shared/ExternalRegisterSettingsUi";

export const SourceColumnsTable = () => {
  const { t } = useTranslation();
  const { parseResponse } = useExternalRegisterImport();
  if (!parseResponse) return null;

  return (
    <Stack spacing={0.75}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
        {t("externalRegister.import.parse.tables.sourceColumns")}
      </Typography>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          borderColor: "divider",
          borderRadius: 1,
          overflowX: "auto",
        }}
      >
        <Table
          size="small"
          sx={{
            minWidth: 720,
            ...compactTableSx,
            "& th": {
              ...compactTableSx["& th"],
              fontSize: "0.72rem",
            },
            "& td": {
              ...compactTableSx["& td"],
              fontSize: "0.78rem",
            },
            "& tbody tr:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 72 }}>{t("externalRegister.import.parse.tables.headers.column")}</TableCell>
              <TableCell>{t("externalRegister.import.parse.tables.headers.detectedLabel")}</TableCell>
              <TableCell>{t("externalRegister.import.parse.tables.headers.sampleValues")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parseResponse.columns.map((column) => (
              <TableRow key={column.index}>
                <TableCell>
                  <Chip
                    size="small"
                    label={column.index + 1}
                    sx={{
                      height: 22,
                      minWidth: 34,
                      borderRadius: 0.75,
                      fontSize: "0.72rem",
                      fontWeight: 800,
                      backgroundColor: "grey.100",
                    }}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: "text.primary", whiteSpace: "nowrap" }}>
                  {column.label}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {column.sampleValues.length > 0 ? column.sampleValues.map((value, index) => (
                      <Chip
                        key={`${column.index}-${value}-${index}`}
                        size="small"
                        variant="outlined"
                        label={value}
                        sx={{
                          maxWidth: 240,
                          height: 22,
                          borderRadius: 0.75,
                          color: "text.secondary",
                          fontSize: "0.72rem",
                          "& .MuiChip-label": {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          },
                        }}
                      />
                    )) : (
                      <Typography variant="caption" color="text.disabled">
                        {t("externalRegister.import.parse.tables.noSamples")}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export const ParseErrorsTable = () => {
  const { t } = useTranslation();
  const { parseResponse } = useExternalRegisterImport();
  if (!parseResponse?.parseErrors.length) return null;

  return (
    <Stack spacing={1}>
      <Alert severity="error" variant="outlined">
        {t("externalRegister.import.parse.errors.alert")}
      </Alert>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1, maxHeight: 240 }}>
        <Table size="small" stickyHeader sx={{ minWidth: 520, ...compactTableSx }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 110 }}>{t("externalRegister.import.parse.errors.table.headers.csvRow")}</TableCell>
              <TableCell>{t("externalRegister.import.parse.errors.table.headers.problem")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parseResponse.parseErrors.map((error, index) => (
              <TableRow key={`${error.rowNumber}-${error.message}-${index}`}>
                <TableCell>{error.rowNumber}</TableCell>
                <TableCell>{error.message}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export const SampleRowsTable = () => {
  const { t } = useTranslation();
  const { parseResponse } = useExternalRegisterImport();
  if (!parseResponse) return null;

  return (
    <Stack spacing={0.75}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
        {t("externalRegister.import.parse.tables.sampleRows")}
      </Typography>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          borderColor: "divider",
          borderRadius: 1,
          overflowX: "auto",
        }}
      >
        <Table
          size="small"
          sx={{
            minWidth: Math.max(760, parseResponse.columns.length * 132),
            tableLayout: "fixed",
            ...compactTableSx,
            "& th": {
              ...compactTableSx["& th"],
              fontSize: "0.68rem",
              lineHeight: 1.2,
            },
            "& td": {
              ...compactTableSx["& td"],
              fontSize: "0.74rem",
              lineHeight: 1.35,
              maxWidth: 180,
            },
            "& tbody tr:nth-of-type(even)": {
              backgroundColor: "grey.50",
            },
          }}
        >
          <TableHead>
            <TableRow>
              {parseResponse.columns.map((column) => (
                <TableCell key={column.index}>
                  <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {column.index + 1}. {column.label}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {parseResponse.sampleRows.slice(0, 3).map((row, rowIndex) => (
              <TableRow key={`sample-${rowIndex}`}>
                {parseResponse.columns.map((column) => {
                  const value = row[column.index] ?? "";
                  return (
                    <TableCell key={column.index}>
                      <Box
                        title={value}
                        sx={{
                          color: value ? "text.primary" : "text.disabled",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {value || t("externalRegister.import.parse.tables.empty")}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};
